import { Order } from '@protos/v2/order';
import {
  CellKeyDownEvent,
  GridApi,
  RowClickedEvent,
  RowDoubleClickedEvent,
  RowDataUpdatedEvent,
  IRowNode,
  ToolPanelVisibleChangedEvent,
} from 'ag-grid-enterprise';
import { useEffect, useRef, useState } from 'react';

export enum ToolPanel {
  NONE = '',
  COLUMNS = 'columns',
  DETAILS = 'details',
}

interface UseOrderDetailsPanelOptions {
  apiClient?: {
    getOrderById: (id: string) => Promise<Order | undefined>;
  };
  enableRowDataUpdateTracking?: boolean;
}

export function useOrderDetailsPanel(options: UseOrderDetailsPanelOptions = {}) {
  const { apiClient, enableRowDataUpdateTracking = false } = options;
  const [selectedRow, setSelectedRow] = useState<Order | null>(null);
  const [visiblePanel, setVisiblePanel] = useState<ToolPanel>(ToolPanel.NONE);
  const selectedRowId = useRef<string | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  const rowUpdateCallbackRef = useRef<((e: RowDataUpdatedEvent) => void) | null>(null);

  const fetchOrderById = async (id: string): Promise<Order | null> => {
    if (!apiClient) return null;
    return (await apiClient.getOrderById(id)) || null;
  };

  const updateGridRow = (order: Order) => {
    const existingRowNode = gridApiRef.current?.getRowNode(order.id);
    if (existingRowNode?.data) {
      const mergedOrder = {
        ...existingRowNode.data,
        ...order,
        user: existingRowNode.data.user,
      };
      gridApiRef.current?.applyTransaction({ update: [mergedOrder] });
    } else {
      gridApiRef.current?.applyTransaction({ update: [order] });
    }
  };

  const refreshOrderAndUpdateGrid = async (id: string): Promise<Order | null> => {
    const order = await fetchOrderById(id);
    if (order) {
      updateGridRow(order);
    }
    return order;
  };

  const onGridApiAvailable = (api: GridApi) => {
    gridApiRef.current = api;

    if (enableRowDataUpdateTracking) {
      rowUpdateCallbackRef.current = (e: RowDataUpdatedEvent) => {
        if (gridApiRef.current && selectedRowId.current) {
          const row: IRowNode<Order> | undefined = gridApiRef.current.getRowNode(selectedRowId.current);

          if (row && row.data) {
            const updated: Order = { ...row.data };
            setSelectedRow({ ...updated });
          }
        }
      };

      api.addEventListener('rowDataUpdated', rowUpdateCallbackRef.current);
      // TODO: track down reason why panel doesn't update if only the above is present
      api.addEventListener('rowDataUpdated', () => {});
    }
  };

  const onRowClicked = async (params: RowClickedEvent<Order, any>) => {
    if (params.data) {
      selectedRowId.current = params.data.id;
      const order = await refreshOrderAndUpdateGrid(params.data.id);
      setSelectedRow(order || params.data);
    } else {
      setSelectedRow(null);
    }
  };

  const onCellKeyDown = async (params: CellKeyDownEvent<Order, any>) => {
    if ((params.event as KeyboardEvent)?.key === 'ArrowDown') {
      const nextRow = params.api.getDisplayedRowAtIndex((params.rowIndex ?? 0) + 1);
      if (nextRow?.data) {
        const order = await refreshOrderAndUpdateGrid(nextRow.data.id);
        setSelectedRow(order || nextRow.data);
      }
    }
    if ((params.event as KeyboardEvent)?.key === 'ArrowUp') {
      const previousRow = params.api.getDisplayedRowAtIndex((params.rowIndex ?? 0) - 1);
      if (previousRow?.data) {
        const order = await refreshOrderAndUpdateGrid(previousRow.data.id);
        setSelectedRow(order || previousRow.data);
      }
    }
  };

  const onRowDoubleClicked = async (params: RowDoubleClickedEvent<Order, any>) => {
    if (params.data) {
      setVisiblePanel(ToolPanel.DETAILS);
      const order = await refreshOrderAndUpdateGrid(params.data.id);
      setSelectedRow(order || params.data);
    }
  };

  const onToolPanelVisibilityChanged = (event: ToolPanelVisibleChangedEvent) => {
    if (event.visible) {
      const panelKey = event.key;
      if (panelKey === 'columns') {
        setVisiblePanel(ToolPanel.COLUMNS);
      } else if (panelKey === 'details') {
        setVisiblePanel(ToolPanel.DETAILS);
      }
    } else {
      setVisiblePanel(ToolPanel.NONE);
    }
  };

  const onRowHighlighted = (selectedRowData: Order | null) => {
    if (selectedRowData) setSelectedRow(selectedRowData);
  };

  useEffect(() => {
    return () => {
      if (gridApiRef.current && rowUpdateCallbackRef.current) {
        gridApiRef.current.removeEventListener('rowDataUpdated', rowUpdateCallbackRef.current);
      }
    };
  }, []);

  return {
    selectedRow,
    visiblePanel,
    setVisiblePanel,
    onGridApiAvailable,
    onRowClicked,
    onCellKeyDown,
    onRowDoubleClicked,
    onRowHighlighted,
    onToolPanelVisibilityChanged,
  };
}
